// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Axios Imports
import api from "@src/api/config"
import { showModalSuccess } from "../../../../redux/modalTypeSuccess"
import { showModalError } from "../../../../redux/modalTypeError"
import {
  getPolicyByProvider,
  getPolicyByProviderAP
} from "../../Providers/store"

const urlBase = "api/providers"

/**
 * Retrive providers catalog
 */
export const getClausesProviders = createAsyncThunk(
  "providers/get",
  /** @type {import("@reduxjs/toolkit").AsyncThunkPayloadCreator<any, ParamType, { state: AppState }>} */
  async (params, { getState }) => {
    const state = getState()
    const { productSelected } = state.products
    const { businessLineSelected } = state.businessLines
    const response = await api.get(`${urlBase}`, {
      params: {
        product: productSelected.value,
        business_line: businessLineSelected?.id,
        ...params
      }
    })
    return {
      params,
      data: response.data
    }
  }
)

/**
 * @description: consultar quotations/project_profile_and_fee_type
 */
export const getProvidersOptionsProjectProfileAndFeeType = createAsyncThunk(
  "providers/getOptions",
  async (params) => {
    const response = await api.get('api/quotations/project_profile_and_fee_type', {
      params: {
        ...params,
        format: "options"
      }
    })
    return {
      params,
      data: response.data
    }
  }
)

/**
 * Retrieve clauses catalog
 */
export const getPolicyTypes = createAsyncThunk(
  "clauses/get",
  async (params = {}, { getState }) => {
    const state = getState()

    const { productSelected } = state.products
    const { businessLineSelected } = state.businessLines

    params.product = productSelected.value
    params.business_line = businessLineSelected?.id

    const response = await api.get("api/policy_types", { params })
    return {
      params,
      data: response.data
    }
  }
)

/**
 * Retrieve clauses by id
 */
export const getPolicyTypeById = createAsyncThunk(
  "clauses/getById",
  async (id) => {
    const response = await api.get(`api/policy_types/${id}`)
    return {
      data: response.data
    }
  }
)

export const updateMainPolicy = createAsyncThunk(
  "policy/updateMain",
  async ({ id, data }, { dispatch }) => {
    const response = await api.put(`api/main_policy/${id}`, data)

    if (response.data.status === "success") {
      const url = `/catalogs/clauses`
      dispatch(showModalSuccess({ message: response.data.message, url }))
    }

    return {
      data: response.data
    }
  }
)

export const getSecondaryPolicy = createAsyncThunk(
  "secondaryPolicy/getByProvider",
  async ({ provider_id, currency_id }) => {
    const response = await api.get(`api/secondary_policy_types/${provider_id}/${currency_id}`)

    return {
      data: response.data
    }
  }
)

export const createSecondaryPolicy = createAsyncThunk(
  "secondaryPolicy/create",
  async (data, { dispatch }) => {
    const response = await api.post(`api/secondary_policy_types`, data)

    if (response.data.status === "success") {
      const url = `/catalogs/clauses`

      dispatch(showModalSuccess({
        message: response.data.message,
        url
      }))
    }

    return {
      data: response.data
    }
  }
)

export const updateSecondaryPolicy = createAsyncThunk(
  "secondaryPolicy/update",
  async ({ id, data }, { dispatch }) => {
    const response = await api.put(`api/secondary_policy/${id}`, data)

    if (response.data.status === "success") {
      const url = `/catalogs/clauses`

      dispatch(showModalSuccess({
        message: response.data.message,
        url
      }))
    }

    return {
      data: response.data
    }
  }
)

export const getProviderById = createAsyncThunk(
  "providers/getById",
  async (id) => {
    const response = await api.get(`${urlBase}/${id}`)
    return {
      data: response.data
    }
  }
)

/**
 * Save clauses, text
 */
export const editPolicyType = createAsyncThunk(
  "clauses/save",
  async ({ id, policy = null, data, url = `goBack` }, { dispatch }) => {
    const response = await api
      .put(`api/policy_types/${id}`, data)
      .catch((err) => {
        dispatch(
          showModalError({ message: JSON.stringify(err.response.data.errors) })
        )
      })
    // console.debug("response", response)
    if (policy) {
      if (policy.plan_id) {
        dispatch(
          getPolicyByProviderAP({
            provider: policy.provider_id,
            currency: policy.currency_id,
            plan: policy.plan_id
          })
        )
      } else {
        dispatch(
          getPolicyByProvider({
            provider: policy.provider_id,
            currency: policy.currency_id
          })
        )
      }

      dispatch(getPolicyTypeById(id))
    }
    dispatch(showModalSuccess({ message: response.data.message, url }))
    return {
      data: response.data
    }
  }
)

export const editProvider = createAsyncThunk(
  "providers/save",
  async ({ id, data }, { dispatch }) => {
    const response = await api.put(`${urlBase}/${id}`, data)
    const url = `goBack`
    dispatch(showModalSuccess({ message: response.data.message, url }))
    return {
      data: response.data
    }
  }
)

export const providerSlice = createSlice({
  name: "providers",
  initialState: {
    data: null,
    options: [],
    policies: [],
    policies_options: [],
    secondary_policies: [],
    loading: false,
    submitLoading: false
  },
  reducers: {
    resetProviders: (state) => {
      state.data = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClausesProviders.fulfilled, (state, action) => {
        // console.debug("getClausesProviders", action.payload)
        if (action.payload?.params?.format === "options") {
          state.options = action.payload.data
        } else {
          state.data = action.payload.data.data
        }
      })
      .addCase(getProvidersOptionsProjectProfileAndFeeType.fulfilled, (state, action) => {
        state.data = action.payload.data
      })
      .addCase(getProviderById.fulfilled, (state, action) => {
        state.data = action.payload.data.data
      })
      .addCase(editProvider.fulfilled, (state, action) => {
        state.data = action.payload.data.data
      })
      .addCase(getPolicyTypes.pending, (state) => {
        state.loading = true
      })
      .addCase(getPolicyTypes.rejected, (state) => {
        state.loading = false
      })
      .addCase(getPolicyTypes.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.params?.format === "options") {
          state.policies_options = action.payload.data
        } else {
          state.policies = action.payload.data.data
        }
      })
      .addCase(getPolicyTypeById.fulfilled, (state, action) => {
        state.policies = action.payload.data.data
      })
      .addCase(updateMainPolicy.fulfilled, (state) => {
        state.submitLoading = false
      })
      .addCase(updateMainPolicy.pending, (state) => {
        state.submitLoading = true
      })
      .addCase(updateMainPolicy.rejected, (state) => {
        state.submitLoading = false
      })
      .addCase(getSecondaryPolicy.pending, (state) => {
        state.loading = true
      })
      .addCase(getSecondaryPolicy.rejected, (state) => {
        state.loading = false
      })
      .addCase(getSecondaryPolicy.fulfilled, (state, action) => {
        state.loading = false
        state.secondary_policies = action.payload.data.data
      })
      .addCase(createSecondaryPolicy.fulfilled, (state) => {
        state.submitLoading = false
      })
      .addCase(createSecondaryPolicy.pending, (state) => {
        state.submitLoading = true
      })
      .addCase(createSecondaryPolicy.rejected, (state) => {
        state.submitLoading = false
      })
      .addCase(updateSecondaryPolicy.fulfilled, (state) => {
        state.submitLoading = false
      })
      .addCase(updateSecondaryPolicy.pending, (state) => {
        state.submitLoading = true
      })
      .addCase(updateSecondaryPolicy.rejected, (state) => {
        state.submitLoading = false
      })
  }
})

export const { resetProviders } = providerSlice.actions

export default providerSlice.reducer
